import type { Compute } from "@iyk/prelude"
import { Data } from "effect"
import type { FetcherSubmitFunction, FormProps, LoaderFunctionArgs } from "react-router"
import {
  Form as ReactRouterForm,
  useActionData,
  useFetcher,
  useNavigation,
  type AppLoadContext,
} from "react-router"
import type { DataWithResponseInit } from "../app/DataWithResponseInit.ts"
import { Outcome } from "../app/Outcome.ts"

interface UseForm<A, E> {
  formErrors: Array<string>
  fieldErrors?: E
  state: FormState<A, E>
  Form: React.ForwardRefExoticComponent<FormProps & React.RefAttributes<HTMLFormElement>>
  isSubmitting: boolean
}

export const useForm = <H extends Handler<unknown>>(): InferUseForm<H> => {
  const data = useActionData<H>()
  const navigation = useNavigation()

  const isSubmitting = navigation.state === "submitting"

  const makeState = (): FormState<InferOutcomeSuccess<H>, InferFieldErrors<H>> => {
    if (isSubmitting) {
      return FormState.Submitting(navigation.formData ?? new FormData())
    }

    if (!data || data instanceof Response) {
      return FormState.Idle()
    }

    return Outcome.match(data, {
      Failure: ({ reasons, fields }) => FormState.Failed(reasons, fields as InferFieldErrors<H>),
      Success: ({ value }) => FormState.Succeeded(value as InferOutcomeSuccess<H>),
    })
  }

  const state = makeState()

  return {
    formErrors: Outcome.isFailure(data) ? data.reasons : [],
    fieldErrors: Outcome.isFailure(data) ? (data.fields as InferFieldErrors<H>) : undefined,
    Form: ReactRouterForm,
    state: state,
    isSubmitting: FormState.isSubmitting(state),
  } as InferUseForm<H>
}

interface UseFetcherForm<A, E> extends UseForm<A, E> {
  submit: FetcherSubmitFunction
  reset: () => void
}

export const useFetcherForm = <H extends Handler<unknown>>(): InferUseFetcherForm<H> => {
  const fetcher = useFetcher<H>()

  const makeState = (): FormState<InferOutcomeSuccess<H>, InferFieldErrors<H>> => {
    switch (fetcher.state) {
      case "idle": {
        if (!fetcher.data) {
          return FormState.Idle()
        }

        return Outcome.match(fetcher.data, {
          Failure: ({ reasons, fields }) =>
            FormState.Failed(reasons, fields as InferFieldErrors<H>),
          Success: ({ value }) => FormState.Succeeded(value as InferOutcomeSuccess<H>),
        })
      }
      case "submitting":
      case "loading": {
        return FormState.Submitting(fetcher.formData ?? new FormData())
      }
    }
  }

  const state = makeState()

  return {
    formErrors: Outcome.isFailure(fetcher.data) ? fetcher.data.reasons : [],
    fieldErrors: Outcome.isFailure(fetcher.data)
      ? (fetcher.data.fields as InferFieldErrors<H>)
      : undefined,
    Form: fetcher.Form,
    state: state,
    isSubmitting: FormState.isSubmitting(state),
    submit: fetcher.submit,
    reset: fetcher.reset,
  } as InferUseFetcherForm<H>
}

export type Handler<A, E = unknown> = (
  args: LoaderFunctionArgs<AppLoadContext>,
) => Promise<DataWithResponseInit<Outcome<A, E>> | Response>

type InferUseForm<H> = H extends (
  args: LoaderFunctionArgs<AppLoadContext>,
) => Promise<DataWithResponseInit<Outcome<infer A, infer E>> | Response>
  ? UseForm<A, E>
  : never

type InferUseFetcherForm<H> = H extends (
  args: LoaderFunctionArgs<AppLoadContext>,
) => Promise<DataWithResponseInit<Outcome<infer A, infer E>> | Response>
  ? UseFetcherForm<A, E>
  : never

type InferOutcomeSuccess<H> = H extends (
  args: LoaderFunctionArgs<AppLoadContext>,
) => Promise<DataWithResponseInit<Outcome<infer A, infer E>> | Response>
  ? A
  : never

type InferFieldErrors<H> = H extends (
  args: LoaderFunctionArgs<AppLoadContext>,
) => Promise<DataWithResponseInit<Outcome<infer A, infer E>> | Response>
  ? E
  : never

type ExtractIntent<H extends Handler<unknown>> = Extract<
  Required<InferFieldErrors<H>>,
  { intent: string }
>["intent"]

export type ActionWithIntent<H extends Handler<unknown>, Intent extends ExtractIntent<H>> = Handler<
  InferOutcomeSuccess<H>,
  Compute<Partial<Extract<Required<InferFieldErrors<H>>, { intent: Intent }>>>
>

export type FormState<A, E> = Data.TaggedEnum<{
  Idle: {}
  Submitting: { formData: FormData }
  Failed: { errors: Array<string>; fields: E }
  Succeeded: { data: A }
}> & {}

export namespace FormState {
  interface FormStateDefinition extends Data.TaggedEnum.WithGenerics<2> {
    readonly taggedEnum: FormState<this["A"], this["B"]>
  }

  const _FormState = Data.taggedEnum<FormStateDefinition>()

  export const Idle = () => _FormState.Idle()
  export const Submitting = (formData: FormData) => _FormState.Submitting({ formData })
  export const Failed = <E>(errors: Array<string>, fields: E) =>
    _FormState.Failed({ errors, fields })
  export const Succeeded = <A>(data: A) => _FormState.Succeeded({ data })

  export const isIdle = <A, E>(state: FormState<A, E>) => _FormState.$is("Idle")(state)
  export const isSubmitting = <A, E>(state: FormState<A, E>) => _FormState.$is("Submitting")(state)
  export const isFailed = <A, E>(state: FormState<A, E>) => _FormState.$is("Failed")(state)
  export const isSucceeded = <A, E>(state: FormState<A, E>) => _FormState.$is("Succeeded")(state)

  export const match = _FormState.$match

  export const matchSuccess = <A, E, B>(state: FormState<A, E>, success: (data: A) => B) => {
    return match(state, {
      Idle: () => null,
      Submitting: () => null,
      Failed: () => null,
      Succeeded: (state) => success(state.data),
    })
  }

  export const matchSuccessOrElse = <A, E, B>(
    state: FormState<A, E>,
    success: (data: A) => B,
    orElse: () => B,
  ) => {
    return match(state, {
      Idle: () => orElse(),
      Submitting: () => orElse(),
      Failed: () => orElse(),
      Succeeded: (state) => success(state.data),
    })
  }
}
